import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Shield,
  Users,
  FileText,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Download,
  Eye,
  BarChart3,
  Loader
} from 'lucide-react'
import AdminLogin from '../components/AdminLogin'
import type { AccessRequest } from '../lib/storage'
import { useTenant } from '../contexts/TenantContext'

const AdminDashboard = () => {
  const navigate = useNavigate()
  const { currentTenant } = useTenant()
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [adminEmail, setAdminEmail] = useState('')
  const [requests, setRequests] = useState<AccessRequest[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [documentsCount, setDocumentsCount] = useState(0)
  const [monthlyViews, setMonthlyViews] = useState(0)
  const [analyticsLoading, setAnalyticsLoading] = useState(false)

  // Track fetch call IDs to prevent stale state updates from concurrent fetches
  const analyticsCallIdRef = useRef(0)

  useEffect(() => {
    // Check if already logged in
    try {
      const session = localStorage.getItem('admin-session')
      if (session) {
        const { email, loginTime } = JSON.parse(session)
        // Check if session is less than 8 hours old
        if (Date.now() - loginTime < 8 * 60 * 60 * 1000) {
          setAdminEmail(email)
          setIsAuthenticated(true)
          // Don't auto-fetch on page load to avoid blocking the UI
          const timerId = setTimeout(() => {
            fetchRequests()
            fetchAnalytics()
          }, 100)

          return () => clearTimeout(timerId)
        } else {
          localStorage.removeItem('admin-session')
        }
      }
    } catch (e) {
      console.error('Session check failed:', e)
      localStorage.removeItem('admin-session')
    }
  }, [])

  // Fetch analytics when tenant changes
  useEffect(() => {
    if (isAuthenticated && currentTenant) {
      const controller = new AbortController()

      fetchAnalytics(controller.signal)

      return () => {
        controller.abort()
      }
    }
  }, [currentTenant, isAuthenticated])

  const handleLogin = (email: string) => {
    setAdminEmail(email)
    setIsAuthenticated(true)
    fetchRequests()
    fetchAnalytics()
  }

  const handleLogout = () => {
    // SECURITY: Clear ALL cached data including sensitive information
    localStorage.clear()
    sessionStorage.clear()

    // Clear all cookies
    document.cookie.split(";").forEach((cookie) => {
      const name = cookie.split("=")[0].trim()
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`
    })

    setIsAuthenticated(false)
    setAdminEmail('')
    setRequests([])

    // Force redirect to clear cached state
    window.location.href = '/'
  }

  const fetchRequests = async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await fetch('/api/admin/requests', {
        headers: {
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(`HTTP ${response.status}: ${errorText}`)
      }

      const data = await response.json()
      setRequests(Array.isArray(data) ? data : [])
    } catch (err) {
      console.error('Fetch requests error:', err)
      setError(err instanceof Error ? err.message : 'Failed to load requests')
      setRequests([]) // Set empty array on error
    } finally {
      setLoading(false)
    }
  }

  const fetchAnalytics = async (signal?: AbortSignal) => {
    if (!currentTenant) return

    // Increment call ID to track this specific fetch request
    const currentCallId = ++analyticsCallIdRef.current

    setAnalyticsLoading(true)
    try {
      // Fetch documents count
      const docsResponse = await fetch('/api/documents', {
        headers: {
          'Content-Type': 'application/json'
        },
        signal
      })

      if (!docsResponse.ok) {
        console.error(`[AdminDashboard] Documents API returned ${docsResponse.status}: ${docsResponse.statusText}`)
      } else {
        const docs = await docsResponse.json()
        // Only update state if this is still the most recent call (prevents stale state)
        if (!signal?.aborted && currentCallId === analyticsCallIdRef.current) {
          setDocumentsCount(Array.isArray(docs) ? docs.length : 0)
        }
      }

      // Fetch analytics for monthly views (last 30 days)
      const analyticsResponse = await fetch(`/api/analytics/get?tenantId=${currentTenant.id}&days=30`, {
        signal
      })

      if (!analyticsResponse.ok) {
        const errorBody = await analyticsResponse.text()
        console.error(`[AdminDashboard] Analytics API returned ${analyticsResponse.status}: ${errorBody}`)
      } else {
        const analytics = await analyticsResponse.json()
        // Only update state if this is still the most recent call (prevents stale state)
        if (!signal?.aborted && currentCallId === analyticsCallIdRef.current) {
          setMonthlyViews(analytics.summary?.totalPageViews || 0)
        }
      }
    } catch (err) {
      // Don't show error if request was aborted
      if (err instanceof Error && err.name === 'AbortError') {
        console.log('Analytics fetch was aborted')
        return
      }
      console.error('Analytics fetch error:', err)
      // Don't show error to user, just keep defaults
    } finally {
      // Only update loading state if this is still the most recent call
      if (!signal?.aborted && currentCallId === analyticsCallIdRef.current) {
        setAnalyticsLoading(false)
      }
    }
  }

  const handleRequestAction = async (requestId: string, action: 'approve' | 'reject') => {
    try {
      const response = await fetch('/api/admin/requests', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          requestId,
          action,
          adminEmail
        })
      })

      if (!response.ok) {
        throw new Error('Failed to update request')
      }

      // Refresh requests
      await fetchRequests()
    } catch (err) {
      console.error('Error updating request:', err)
      setError(err instanceof Error ? err.message : 'Failed to update request')
    }
  }

  if (!isAuthenticated) {
    return <AdminLogin onLogin={handleLogin} />
  }

  const pendingRequestsCount = requests.filter(r => r.status === 'pending').length
  const activeSessionsCount = requests.filter(r => r.status === 'approved').length

  const stats = [
    {
      name: 'Pending Requests',
      value: pendingRequestsCount.toString(),
      icon: Clock,
      color: 'bg-yellow-100 text-yellow-800',
      onClick: () => navigate('/admin/full#requests')
    },
    {
      name: 'Active Sessions',
      value: activeSessionsCount.toString(),
      icon: Users,
      color: 'bg-green-100 text-green-800',
      onClick: () => navigate('/admin/full#sessions')
    },
    {
      name: 'Documents',
      value: documentsCount.toString(),
      icon: FileText,
      color: 'bg-blue-100 text-blue-800',
      onClick: () => navigate('/admin/full#documents')
    },
    {
      name: 'This Month Views',
      value: monthlyViews.toString(),
      icon: Eye,
      color: 'bg-purple-100 text-purple-800',
      onClick: () => navigate('/admin/full#analytics')
    }
  ]

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <Clock className="h-4 w-4 text-yellow-600" />
      case 'approved':
        return <CheckCircle className="h-4 w-4 text-green-600" />
      case 'rejected':
        return <XCircle className="h-4 w-4 text-red-600" />
      default:
        return <AlertTriangle className="h-4 w-4 text-gray-600" />
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800'
      case 'approved':
        return 'bg-green-100 text-green-800'
      case 'rejected':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <Shield className="h-8 w-8 text-primary-600" />
              <h1 className="ml-3 text-2xl font-bold text-gray-900">Admin Dashboard</h1>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600">Logged in as: {adminEmail}</span>
              <button className="btn-secondary">
                <Download className="h-4 w-4 mr-2" />
                Export Logs
              </button>
              <button onClick={handleLogout} className="text-red-600 hover:text-red-700">Logout</button>
              <a href="/" className="text-primary-600 hover:text-primary-700">← Back to Trust Center</a>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {stats.map((stat, index) => {
            const IconComponent = stat.icon
            return (
              <div
                key={index}
                onClick={stat.onClick}
                className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 cursor-pointer hover:shadow-lg transition-shadow"
              >
                <div className="flex items-center">
                  <div className={`p-2 rounded-md ${stat.color}`}>
                    <IconComponent className="h-6 w-6" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">{stat.name}</p>
                    <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Recent Requests */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200 cursor-pointer hover:bg-gray-50 transition-colors" onClick={() => navigate('/admin/full#requests')}>
              <h2 className="text-lg font-semibold text-gray-900">Recent Access Requests</h2>
            </div>
            <div className="divide-y divide-gray-200">
              {loading ? (
                <div className="p-6 text-center">
                  <Loader className="h-6 w-6 animate-spin mx-auto mb-2" />
                  <p className="text-sm text-gray-500">Loading requests...</p>
                </div>
              ) : error ? (
                <div className="p-6 text-center">
                  <AlertTriangle className="h-6 w-6 text-red-500 mx-auto mb-2" />
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              ) : requests.length === 0 ? (
                <div className="p-6 text-center">
                  <p className="text-sm text-gray-500">No requests found</p>
                </div>
              ) : (
                requests.slice(0, 5).map((request) => (
                  <div key={request.id} className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center">
                          {getStatusIcon(request.status)}
                          <div className="ml-2">
                            <p className="text-sm font-medium text-gray-900">{request.email}</p>
                            <p className="text-sm text-gray-600">{request.company}</p>
                          </div>
                        </div>
                        <div className="mt-2">
                          <p className="text-sm text-gray-600">
                            Requested: {request.targets.join(', ') || 'General access'}
                          </p>
                          <p className="text-sm text-gray-600 mt-1">
                            Reason: {request.reason}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            {new Date(request.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(request.status)}`}>
                        {request.status}
                      </span>
                    </div>
                    {request.status === 'pending' && (
                      <div className="mt-4 flex space-x-2">
                        <button
                          onClick={() => handleRequestAction(request.id, 'approve')}
                          className="btn-primary text-sm py-1 px-3"
                        >
                          Approve
                        </button>
                        <button
                          onClick={() => handleRequestAction(request.id, 'reject')}
                          className="btn-secondary text-sm py-1 px-3"
                        >
                          Reject
                        </button>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
            <div className="p-4 border-t border-gray-200">
              <button
                onClick={() => navigate('/admin/full#requests')}
                className="text-sm text-primary-600 hover:text-primary-700"
              >
                View All Requests →
              </button>
            </div>
          </div>

          {/* Quick Analytics */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">Analytics Overview</h2>
                <BarChart3 className="h-5 w-5 text-gray-400" />
              </div>
            </div>
            <div className="p-6">
              <div className="space-y-6">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">Most Requested Documents</span>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">SOC 2 Type II</span>
                      <span className="text-sm font-medium text-gray-900">34 requests</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Security Policies</span>
                      <span className="text-sm font-medium text-gray-900">28 requests</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Pen Test Results</span>
                      <span className="text-sm font-medium text-gray-900">22 requests</span>
                    </div>
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">Request Sources</span>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Enterprise (&gt;1000 employees)</span>
                      <span className="text-sm font-medium text-gray-900">67%</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Mid-market (100-1000)</span>
                      <span className="text-sm font-medium text-gray-900">28%</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Small business (&lt;100)</span>
                      <span className="text-sm font-medium text-gray-900">5%</span>
                    </div>
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">Approval Rate</span>
                  </div>
                  <div className="flex items-center">
                    <div className="flex-1 bg-gray-200 rounded-full h-2">
                      <div className="bg-green-600 h-2 rounded-full" style={{ width: '84%' }}></div>
                    </div>
                    <span className="ml-2 text-sm font-medium text-gray-900">84%</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="p-4 border-t border-gray-200">
              <button className="text-sm text-primary-600 hover:text-primary-700">
                View Detailed Analytics →
              </button>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Content Management</h3>
            <div className="space-y-3">
              <button className="w-full text-left btn-secondary">Upload New Document</button>
              <button className="w-full text-left btn-secondary">Manage Categories</button>
              <button className="w-full text-left btn-secondary">Update Policies</button>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Access Control</h3>
            <div className="space-y-3">
              <button
                onClick={() => window.location.href = '/admin#requests'}
                className="w-full text-left btn-secondary"
              >
                Review Pending Requests
              </button>
              <button className="w-full text-left btn-secondary">Manage User Sessions</button>
              <button className="w-full text-left btn-secondary">Access Recertification</button>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Settings</h3>
            <div className="space-y-3">
              <button className="w-full text-left btn-secondary">NDA Templates</button>
              <button className="w-full text-left btn-secondary">Approval Policies</button>
              <button className="w-full text-left btn-secondary">Integrations</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AdminDashboard